<!--
  Forbrukerrådet - Kommunetesten
  @link https://gitlab01.copyleft.no/FBR/kommunetesten-new
  @author Anders Evenrud <anders.evenrud@copyleft.no>
-->
<template>
  <div class="view__municipality">
    <template v-if="municipality && county">
      <section>
        <article class="view__municipality__scores">
          <header>
            <Heading :year="year">
              <Shield :municipality="municipality.id" :county="county.id" :style="{marginRight: '0.5em'}" :large="true" :title="this.municipality.name" />
              {{ this.municipality.name }} kommune
            </Heading>

            <Dropdown
              placeholder="Velg år"
              name="year"
              :value="String(year)"
              :items="yearsDropdown"
              @onchange="onYearChange" />
          </header>

          <Positions :items="positions" />
        </article>

        <article class="view__municipality__stats">
          <Heading>Poeng</Heading>

          <Statistics :items="stats" :key="`${municipality.id}-${year}`">
            <Circles :items="circles" />
          </Statistics>
        </article>

        <!-- Issue #12 - Requested removal of map
        <article class="view__municipality__map">
          <Heading :year="year">Fylkeskart - {{ this.county.name }}</Heading>

          <Map :key="municipality.id" :county="county" :municipality="municipality" :data="tableRows" />
        </article>
        -->
      </section>

      <nav>
        <Heading :year="year">
          <Shield :county="county.id" :style="{marginRight: '0.5em'}" :large="true" :name="this.county.name" />
          Fylkesoversikt - {{ this.county.name }}
        </Heading>

        <Table :year="year" :county="county" :rows="tableRows" :active="municipality.id" :rowsPerPage="100" @click="onTableClick" />
      </nav>
    </template>

    <template v-else>
      <ErrorBox
        title="Fant ingen data for valgt kommune"
        :message="`Kommunen kan ha blitt sammenslått med et annen fylke, eller vi fant ingen data for ${this.year}`" />
    </template>
  </div>
</template>

<style lang="scss" scoped>
@import "../assets/variables";

article {
  margin-bottom: 3em;
}

.circles {
  padding: 1em;
}

@media (max-width: $responsive-break) {
  .view__municipality__map {
    display: none;
  }

  header {
    margin-bottom: 1em;
  }
}

@media (min-width: $responsive-break) {
  header {
    display: flex;
    align-items: center;

    .heading {
      flex: 1 1;
    }

    .dropdown {
      flex: 0 0;
    }
  }

  .circles {
    margin-top: 1em;
  }

  /* Issue #12 - Requested removal of map
  section {
    display: flex;

    & > article {
      flex: 1 1 50%;

      &:first-child {
        margin-right: 1em;
      }

      &:last-child {
        margin-left: 1em;
      }
    }
  }
  */
}
</style>

<script>
// import Map from '../components/Map.vue'
import Table from '../components/Table.vue'
import Shield from '../components/Shield.vue'
import Circles from '../components/Circles.vue'
import Heading from '../components/Heading.vue'
import ErrorBox from '../components/ErrorBox.vue'
import Dropdown from '../components/Dropdown.vue'
import Positions from '../components/Positions.vue'
import Statistics from '../components/Statistics.vue'
import { mapGetters } from 'vuex'

const tree = [{
  key: 'sub_0',
  label: 'Barn og skole',
  items: [{
    key: 'sub_0_1',
    label: 'Barnehage'
  }, {
    key: 'sub_0_2',
    label: 'SFO'
  }, {
    key: 'sub_0_3',
    label: 'Skole'
  }]
}, {
  key: 'sub_1',
  label: 'Tekniske tjenester',
  items: [{
    key: 'sub_1_1',
    label: 'Byggesak'
  }, {
    key: 'sub_1_2',
    label: 'Vann, avløp, renovasjon'
  }, {
    key: 'sub_1_3',
    label: 'Energi'
  }]
}, {
  key: 'sub_2',
  label: 'Sosiale tjenester',
  items: [{
    key: 'sub_2_1',
    label: 'Økonomiske ytelser'
  }, {
    key: 'sub_2_2',
    label: 'Rådgiving'
  }, {
    key: 'sub_2_3',
    label: 'Barnevern'
  }]
}, {
  key: 'sub_3',
  label: 'Pleie og omsorg',
  items: [{
    key: 'sub_3_1',
    label: 'Hjemmehjelp'
  }, {
    key: 'sub_3_2',
    label: 'Sykehjem'
  }]
}, {
  key: 'sub_4',
  label: 'Helse',
  items: [{
    key: 'sub_4_1',
    label: 'Kommunehelsetjeneste'
  }]
}, {
  key: 'sub_5',
  label: 'Fritid og kultur',
  items: [{
    key: 'sub_5_1',
    label: 'Kultur'
  }, {
    key: 'sub_5_2',
    label: 'Idrett'
  }]
}, {
  key: 'sub_6',
  label: 'Informasjonstjenester',
  items: [{
    key: 'sub_6_1',
    label: 'Faktaopplysninger'
  }, {
    key: 'sub_6_2',
    label: 'Kontaktinfo'
  }]
}]

export default {
  props: {
    year: Number,
    county: Object,
    municipality: Object,
    embedded: Boolean
  },

  components: {
    // Map,
    Table,
    Shield,
    Circles,
    Heading,
    ErrorBox,
    Dropdown,
    Positions,
    Statistics
  },

  computed: {
    ...mapGetters([
      'yearsDropdown'
    ]),

    statsData () {
      return this.$store.getters
        .municipalityStatsById(this.municipality.id, this.year)
    },

    tableRows () {
      return this.$store.getters
        .municipalitiesByQuery(this.county.id, this.year)
    },

    stats () {
      const data = this.statsData
      const found = this.tableRows.find(iter => iter.kid === this.municipality.id)

      return tree.map(item => ({
        label: item.label || item.key,
        score: found ? parseInt(data[item.key], 10) : NaN,
        measured: !!found,
        items: item.items.map(subitem => ({
          label: subitem.label || subitem.key,
          score: found ? parseInt(data[subitem.key], 10) : NaN
        }))
      }))
    },

    positions () {
      const found = this.tableRows.find(iter => iter.kid === this.municipality.id)

      return [{
        label: 'Norgesplassering',
        score: found ? found.countryPosition : -1,
        max: this.$store.getters
          .municipalityCount(this.year)
      }, {
        label: 'Fylkesplassering',
        score: found ? found.countyPosition : -1,
        max: this.tableRows.length
      }]
    },

    circles () {
      return [{
        label: this.municipality.name,
        score: this.statsData.score
      }, {
        label: 'Fylkessnitt',
        score: this.$store.getters
          .countyScoreAverage(this.county.id, this.year)
      }, {
        label: 'Landssnitt',
        score: this.$store.getters
          .countryScoreAverage(this.year)
      }]
    }
  },

  methods: {
    onYearChange (ev, item) {
      this.$router.push({
        name: 'municipality',
        params: {
          kid: this.municipality.id
        },
        query: {
          year: item.value || ''
        }
      })
    },

    onTableClick (ev, row) {
      const query = isNaN(this.year) ? {} : { year: this.year }
      const link = this.municipalityLink(row.kid, query)

      this.$router.push(link)
    }
  }
}
</script>
